import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosNoAuth } from "../config/axiosInstance";
import { skills } from "../utilities/constants";
import { NINJACURATE_BACKEND_BASE_URL } from "../config/api_config";

export const fetchSkillName = createAsyncThunk(
    'fetchSkillName/skillDetailsSlice',
    async ({ skill_slug }) => {
        const response = await axiosNoAuth.get('https://xgfy-czuw-092q.m2.xano.io/api:ZZCtun0K/fetch_skill_name_for_personalize', {
            params: {
                skill_slug
            }
        })
        return response.data
    }
)

export const fetchRelatedJobs = createAsyncThunk(
    'fetchRelatedJobs',
    async ({ subject }) => {
        try {
            const response = await axiosNoAuth.post('https://certified-backend.learntube.ai/job_names', {
                subject
            })
            return response.data
        } catch (error) {
            console.log(error.message)
        }
    }
)

export const fetchAnalysisReport = createAsyncThunk(
    'fetchAnalysisReport',
    async ({ difficultyLevel, correctCount, incorrectCount, subject }) => {
        try {
            const response = await axiosNoAuth.post('https://certified-backend.learntube.ai/personalize_ranks', {
                difficultyLevel, correctCount, incorrectCount, subject
            })
            return response.data
        } catch (error) {
            console.log(error.message)
        }
    }
)

export const checkTrialedUser = createAsyncThunk(
    'checkTrialedUser',
    async ({ jwt }) => {
        try {
            const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/trial_check`, {
                params:{
                    jwt
                }
            })
            return response.data?.data
        } catch (error) {
            console.log(error.message)
        }
    }
)

export const updateUserChallenge = createAsyncThunk(
    'updateUserChallenge',
    async ({ session_id, challenge }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/goal`, { session_id, goal : challenge })
            return response.data
        }
        catch (error) {
            const message = error?.response?.data || error?.message || 'Failed to save goal'
            return rejectWithValue(message)
        }
    }
)

export const updateUserTargetRole = createAsyncThunk(
    'updateUserTargetRole',
    async ({ session_id, role }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/role`, { session_id, role })
            return response.data
        } catch (error) {
            const message = error?.response?.data || error?.message || 'Failed to save role'
            return rejectWithValue(message)
        }
    }
)

export const saveQuizAnswer = createAsyncThunk(
    'saveQuizAnswer',
    async ({ session_id, question, selected_option }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/quiz-answer`, {
                session_id,
                question,
                selected_option
            }, {
                headers: {
                    'accept': '*/*',
                    'Content-Type': 'application/json'
                }
            })
            return response.data
        } catch (error) {
            const message = error?.response?.data || error?.message || 'Failed to save answer'
            return rejectWithValue(message)
        }
    }
)

export const saveUserDetailsForSession = createAsyncThunk(
    'saveUserDetailsForSession',
    async ({ session_id, name, email, phone }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/user-details`, {
                session_id,
                name,
                email,
                phone
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            return response.data
        } catch (error) {
            const message = error?.response?.data || error?.message || 'Failed to save user details'
            return rejectWithValue(message)
        }
    }
)

export const updateUserExperienceLevel = createAsyncThunk(
    'updateUserExperienceLevel',
    async ({ session_id, experience_level }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/experience`, { session_id, experience_level })
            return response.data
        }
        catch (error) {
            console.log(error.message)
        }
    }
)

export const saveSubskillScores = createAsyncThunk(
    'saveSubskillScores',
    async ({ session_id, subskill_scores }, { rejectWithValue }) => {
        try {
            console.log('Saving subskill scores:', { session_id, subskill_scores })
            
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/subskill-scores`, {
                session_id,
                subskill_scores
            }, {
                headers: {
                    'accept': '*/*',
                    'Content-Type': 'application/json'
                }
            })
            
            console.log('Subskill scores saved successfully:', response.data)
            return response.data
        } catch (error) {
            console.error('Error saving subskill scores:', error.message)
            return rejectWithValue(error.message)
        }
    }
)

export const fetchQuestionsWithPolling = createAsyncThunk(
    'fetchQuestionsWithPolling',
    async ({ session_id }, { rejectWithValue }) => {
        const maxAttempts = 30; // Maximum polling attempts
        const pollingInterval = 2000; // 2 seconds between polls
        
        const poll = async (attempt = 1) => {
            try {
                console.log(`Polling attempt ${attempt} for questions...`);
                
                const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/questions?session_id=${session_id}`, {
                    headers: {
                        'accept': 'application/json'
                    }
                });
                
                // If response is 200 and has data, stop polling
                if (response.status === 200 && response.data) {
                    console.log('Questions received successfully:', response.data);
                    return response.data;
                }
                
                // If not ready yet and we haven't exceeded max attempts, continue polling
                if (attempt < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, pollingInterval));
                    return poll(attempt + 1);
                }
                
                // Max attempts reached
                throw new Error(`Questions not ready after ${maxAttempts} attempts`);
                
            } catch (error) {
                console.log(`Polling error on attempt ${attempt}:`, error.message);
                
                // If it's a network error or non-200 status and we haven't exceeded max attempts, continue polling
                if (attempt < maxAttempts && (error.response?.status !== 200 || !error.response)) {
                    await new Promise(resolve => setTimeout(resolve, pollingInterval));
                    return poll(attempt + 1);
                }
                
                // If we've exceeded max attempts or it's a different error, reject
                throw error;
            }
        };
        
        try {
            return await poll();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

export const fetchSubskillsWithPolling = createAsyncThunk(
    'fetchSubskillsWithPolling',
    async ({ session_id }, { rejectWithValue }) => {
        const maxAttempts = 30; // Maximum polling attempts
        const pollingInterval = 2000; // 2 seconds between polls
        
        const poll = async (attempt = 1) => {
            try {
                console.log(`Polling attempt ${attempt} for subskills...`);
                
                const response = await axiosNoAuth.get(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/subskills?session_id=${session_id}`, {
                    headers: {
                        'accept': 'application/json'
                    }
                });
                
                // If response is 200 and has data, stop polling
                if (response.status === 200 && response.data) {
                    console.log('Subskills received successfully:', response.data);
                    return response.data;
                }
                
                // If not ready yet and we haven't exceeded max attempts, continue polling
                if (attempt < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, pollingInterval));
                    return poll(attempt + 1);
                }
                
                // Max attempts reached
                throw new Error(`Subskills not ready after ${maxAttempts} attempts`);
                
            } catch (error) {
                console.log(`Polling error on attempt ${attempt}:`, error.message);
                
                // If it's a network error or non-200 status and we haven't exceeded max attempts, continue polling
                if (attempt < maxAttempts && (error.response?.status !== 200 || !error.response)) {
                    await new Promise(resolve => setTimeout(resolve, pollingInterval));
                    return poll(attempt + 1);
                }
                
                // If we've exceeded max attempts or it's a different error, reject
                throw error;
            }
        };
        
        try {
            return await poll();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)



export const updateUserHelpLevel = createAsyncThunk(
    'updateUserExperienceLevel',
    async ({ session_id, style }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/style`, { session_id, style })
            return response.data
        }
        catch (error) {
            console.log(error.message)
        }
    }
)



export const updateUserSpeedLevel = createAsyncThunk(
    'updateUserExperienceLevel',
    async ({ session_id, timeline }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/timeline`, { session_id, timeline })
            return response.data
        }
        catch (error) {
            console.log(error.message)
        }
    }
)


export const updateUserTimeLevel = createAsyncThunk(
    'updateUserExperienceLevel',
    async ({ session_id, weekly_time }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/weekly_time`, { session_id, weekly_time })
            return response.data
        }
        catch (error) {
            console.log(error.message)
        }
    }
)


export const updateUserRoleSkill = createAsyncThunk(
    'updateUserExperienceLevel',
    async ({ session_id, skill }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/session/skill`, { session_id, skill })
            return response.data
        }
        catch (error) {
            console.log(error.message)
        }
    }
)

export const fetchRoleSkills = createAsyncThunk(
    'fetchRoleSkills',
    async ({ role }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/role/skills`, {
                role
            }, {
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            })
            return response.data
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Failed to fetch role skills'
            return rejectWithValue(message)
        }
    }
)

export const fetchTestimonials = createAsyncThunk(
    'fetchTestimonials',
    async ({ subject_name }, { rejectWithValue }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/personalise/subject/testimonials`, {
                subject_name
            }, {
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            })
            return response.data
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Failed to fetch testimonials'
            return rejectWithValue(message)
        }
    }
)

export const courseAfterPaid = createAsyncThunk(
    'courseAfterPaid',
    async ({ session_id ,user_id , subject_name }) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/trigger_course_curation_paid`, { session_id, user_id , subject_name })
            return response.data

        }
    catch (error) {
        console.log(error.message)
    }
}
)

export const dispatchRazorPayStatus = createAsyncThunk(
    'dispatchRazorPayStatus',
    async ({ uuid, payment_id, order_id, isError, razorpay_signature, errorObj }) => {
        try {
            const response = await axiosNoAuth.post("https://xgfy-czuw-092q.m2.xano.io/api:ZZCtun0K/payment_status",
                {
                    uid: uuid,
                    payment_id:payment_id, 
                    order_id:order_id, 
                    isError:isError, 
                    razorpay_signature:razorpay_signature, 
                    errorObj:errorObj
                }
            )
            return response.data
        } catch (error) {
            console.log(error.message)
        }
    }
)

export const trackUserVisit = createAsyncThunk(
    'trackUserVisit',
    async ({ visitor_uuid , page_path}) => {
        try {
            const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/analytics/page-visit`, { visitor_uuid , page_path : page_path ? page_path : 'landing_page' })
            return response.data
        }
        catch (error) {
            console.log(error.message)
        }
    }
)


const initialState = {
    skillName: "",
    skillLevel: "",
    skillDescription: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    showCourseLoader: false,
    visitedAnalysisPage: false,
    relatedJobs: [],
    savedResponse: false,
    analysisReport: {},
    trialedUser: {
        show: ''
    },
    challenge: "",
    isChallengeSelected: false,
    isTargetRoleSelected: false,
    targetRole: "",
    experienceLevel: "",
    helpLevel: "",
    speedLevel: "",
    timeLevel: "",
    isRoleFlow : false,
    roleName: "",
    subskills: null,
    subskillsLoading: false,
    subskillsError: null,
    subskillsSaving: false,
    subskillsSaveError: null,
    questions: null,
    questionsLoading: false,
    questionsError: null,
    mcqAnswers: {},
    roleSkills: [],
    roleSkillsLoading: false,
    roleSkillsError: null,
    testimonials: [],
    testimonialsLoading: false,
    testimonialsError: null,

}

const skillDetailsSlice = createSlice({
    name: "skillDetails",
    initialState,
    reducers: {
        setUserSkillDetails: (state, action) => {
            const { skillName, skillLevel, skillDescription, utm_source, utm_medium, utm_campaign } = action.payload;
            state.skillName = skillName;
            state.skillLevel = skillLevel;
            state.skillDescription = skillDescription
            state.utm_source = utm_source
            state.utm_medium = utm_medium
            state.utm_campaign = utm_campaign
        },
        setUserSkillName: (state, action) => {
            const { skillName } = action.payload;
            state.skillName = skillName;
        },
        setUserSkillNameAndSkillLevel: (state, action) => {
            const { skillName, skillLevel } = action.payload
            state.skillName = skillName
            state.skillLevel = skillLevel
        },
        setUserSkill: (state, action) => {
            const { skillName, skillLevel, skillDescription } = action.payload
            state.skillName = skillName
            state.skillLevel = skillLevel;
            state.skillDescription = skillDescription
        },
        setUserSkillLevel: (state, action) => {
            const { skillLevel, skillDescription } = action.payload
            state.skillLevel = skillLevel
            state.skillDescription = skillDescription
        },
        setUTMDetails: (state, action) => {
            const { utm_source, utm_medium, utm_campaign } = action.payload
            state.utm_source = utm_source
            state.utm_campaign = utm_campaign
            state.utm_medium = utm_medium
        },
        setShowCourseLoader: (state, action) => {
            state.showCourseLoader = action.payload
        },
        setVisitedAnalysisPage: (state, action) => {
            state.visitedAnalysisPage = action.payload
        },
        setSavedResponse: (state, action) => {
            state.savedResponse = action.payload
        },
        setChallenge: (state, action) => {
            state.challenge = action.payload
        },
        setTargetRole: (state, action) => {
            state.targetRole = action.payload
        },
        setExperienceLevel: (state, action) => {
            state.experienceLevel = action.payload
        },
        setHelpLevel: (state, action) => {
            state.helpLevel = action.payload
        },
        setSpeedLevel: (state, action) => {
            state.speedLevel = action.payload
        },
        setTimeLevel: (state, action) => {
            state.timeLevel = action.payload
        },
        setIsRoleFlow: (state, action) => {
            state.isRoleFlow = action.payload
        },
        setRoleName: (state, action) => {
            state.roleName = action.payload
        },
        updateSubskillLevel: (state, action) => {
            const { skillId, level } = action.payload
            if (state.subskills) {
                const skillIndex = state.subskills.findIndex(skill => skill.id === skillId)
                if (skillIndex !== -1) {
                    state.subskills[skillIndex].level = level
                }
            }
        },
        setIsChallengeSelected: (state, action) => {
            state.isChallengeSelected = action.payload
        },
        setIsTargetRoleSelected: (state, action) => {
            state.isTargetRoleSelected = action.payload
        },
        setMcqAnswer: (state, action) => {
            const { questionIndex, answerIndex } = action.payload
            // Ensure mcqAnswers exists
            if (!state.mcqAnswers) {
                state.mcqAnswers = {}
            }
            state.mcqAnswers[questionIndex] = answerIndex
        },
        clearMcqAnswers: (state) => {
            state.mcqAnswers = {}
        }
    },
    extraReducers: (builder)=> {
        builder.addCase(fetchSkillName.fulfilled, (state, action) => {
            state.skillName = action?.payload?.['skill-name']
        })
        builder.addCase(fetchRelatedJobs.fulfilled, (state, action) => {
            state.relatedJobs = action.payload
        })
        builder.addCase(fetchAnalysisReport.fulfilled, (state, action) => {
            state.analysisReport = action.payload
        })
        builder.addCase(checkTrialedUser.fulfilled, (state, action) => {
            state.trialedUser = action.payload
        })
        // Subskills polling reducers
        builder.addCase(fetchSubskillsWithPolling.pending, (state) => {
            state.subskillsLoading = true
            state.subskillsError = null
        })
        builder.addCase(fetchSubskillsWithPolling.fulfilled, (state, action) => {
            state.subskillsLoading = false
            state.subskills = action.payload?.subskills?.map((subskill , index) => ({
                id: index + 1,
                name: subskill.name,
                level: null
            }))
            state.subskillsError = null
        })
        builder.addCase(fetchSubskillsWithPolling.rejected, (state, action) => {
            state.subskillsLoading = false
            state.subskillsError = action.payload || 'Failed to fetch subskills'
        })
        // Save subskill scores reducers
        builder.addCase(saveSubskillScores.pending, (state) => {
            state.subskillsSaving = true
            state.subskillsSaveError = null
        })
        builder.addCase(saveSubskillScores.fulfilled, (state, action) => {
            state.subskillsSaving = false
            state.subskillsSaveError = null
        })
        builder.addCase(saveSubskillScores.rejected, (state, action) => {
            state.subskillsSaving = false
            state.subskillsSaveError = action.payload || 'Failed to save subskill scores'
        })
        // Questions polling reducers
        builder.addCase(fetchQuestionsWithPolling.pending, (state) => {
            state.questionsLoading = true
            state.questionsError = null
        })
        builder.addCase(fetchQuestionsWithPolling.fulfilled, (state, action) => {
            state.questionsLoading = false
            state.questions = action.payload?.questions
            state.questionsError = null
        })
        builder.addCase(fetchQuestionsWithPolling.rejected, (state, action) => {
            state.questionsLoading = false
            state.questionsError = action.payload || 'Failed to fetch questions'
        })
        // Role skills reducers
        builder.addCase(fetchRoleSkills.pending, (state) => {
            state.roleSkillsLoading = true
            state.roleSkillsError = null
        })
        builder.addCase(fetchRoleSkills.fulfilled, (state, action) => {
            state.roleSkillsLoading = false
            state.roleSkills = action.payload?.skills || []
            state.roleSkillsError = null
        })
        builder.addCase(fetchRoleSkills.rejected, (state, action) => {
            state.roleSkillsLoading = false
            state.roleSkillsError = action.payload || 'Failed to fetch role skills'
        })
        // Testimonials reducers
        builder.addCase(fetchTestimonials.pending, (state) => {
            state.testimonialsLoading = true
            state.testimonialsError = null
        })
        builder.addCase(fetchTestimonials.fulfilled, (state, action) => {
            state.testimonialsLoading = false
            state.testimonials = action.payload?.testimonials || []
            state.testimonialsError = null
        })
        builder.addCase(fetchTestimonials.rejected, (state, action) => {
            state.testimonialsLoading = false
            state.testimonialsError = action.payload || 'Failed to fetch testimonials'
        })
    }

})

export const { setUserSkillName, setUserSkill, setUTMDetails, setUserSkillLevel, setUserSkillDetails, setShowCourseLoader, setVisitedAnalysisPage, setUserSkillNameAndSkillLevel, setSavedResponse, setChallenge, setTargetRole, setExperienceLevel, updateSubskillLevel, setIsChallengeSelected, setIsTargetRoleSelected, setMcqAnswer, clearMcqAnswers, setHelpLevel, setSpeedLevel, setTimeLevel, setIsRoleFlow, setRoleName } = skillDetailsSlice.actions
export const selectSkillName = (state) => state.skillDetails?.skillName
export const selectUserSkillDetails = (state) => state.skillDetails
export const selectShowCourseLoader = (state) => state.skillDetails.showCourseLoader
export const selectVisitedAnalysisPage = (state) => state.skillDetails.visitedAnalysisPage
export const selectRelatedJobs = (state) => state.skillDetails.relatedJobs
export const selectSavedResponse = (state) => state.skillDetails.savedResponse
export const selectAnalysisReport = (state) => state.skillDetails.analysisReport
export const selectTrialedUsers = (state) => state.skillDetails.trialedUser
export const selectOnboardingStep1Data = (state) => state.skillDetails.onboardingStep1
export const selectChallenge = (state) => state.skillDetails.challenge
export const selectTargetRole = (state) => state.skillDetails.targetRole
export const selectExperienceLevel = (state) => state.skillDetails.experienceLevel
export const selectSubskills = (state) => state.skillDetails.subskills
export const selectSubskillsLoading = (state) => state.skillDetails.subskillsLoading
export const selectSubskillsError = (state) => state.skillDetails.subskillsError
export const selectSubskillsSaving = (state) => state.skillDetails.subskillsSaving
export const selectSubskillsSaveError = (state) => state.skillDetails.subskillsSaveError
export const selectQuestions = (state) => state.skillDetails.questions
export const selectQuestionsLoading = (state) => state.skillDetails.questionsLoading
export const selectQuestionsError = (state) => state.skillDetails.questionsError
export const selectIsChallengeSelected = (state) => state.skillDetails.isChallengeSelected
export const selectIsTargetRoleSelected = (state) => state.skillDetails.isTargetRoleSelected
export const selectMcqAnswers = (state) => state.skillDetails.mcqAnswers || {}
export const selectHelpLevel = (state) => state.skillDetails.helpLevel
export const selectSpeedLevel = (state) => state.skillDetails.speedLevel
export const selectTimeLevel = (state) => state.skillDetails.timeLevel
export const selectIsRoleFlow = (state) => state.skillDetails.isRoleFlow
export const selectRoleName = (state) => state.skillDetails.roleName
export const selectRoleSkills = (state) => state.skillDetails.roleSkills
export const selectRoleSkillsLoading = (state) => state.skillDetails.roleSkillsLoading
export const selectRoleSkillsError = (state) => state.skillDetails.roleSkillsError
export const selectTestimonials = (state) => state.skillDetails.testimonials
export const selectTestimonialsLoading = (state) => state.skillDetails.testimonialsLoading
export const selectTestimonialsError = (state) => state.skillDetails.testimonialsError
export default skillDetailsSlice.reducer
